import { useEffect, useRef, useState } from "react";
import { useDemo } from "../zustand/DemoZustand";
import Reiter, { Tafel } from "../bausteine/Reiter";
import Aufgabenbrett from "../bausteine/Aufgabenboard";
import Aufgabenkarte from "../bausteine/Aufgabenkarte";
import { Liste, Raster } from "../bausteine/Symbole";
import { gehoertZuMir } from "../hilfen/auswertung";

/*
  Die Aufgaben der Fraktion — zwei Blickwinkel auf denselben Bestand.

    Dein Board       alles, was DICH angeht: wofür du verantwortlich bist
                     und was du beobachtest
    Fraktionsboard   alles, was MEHRERE betrifft — der Gruppenspiegel

  Es sind keine zwei Sammlungen. Eine zugewiesene Aufgabe steht in beiden:
  im Fraktionsboard, damit alle Bescheid wissen, und im eigenen, weil man
  sie erledigen muss. Dieselbe Karte, nicht zwei.

  An einer Aufgabe hängen zwei Angaben: die **Verantwortung** (genau eine
  Person, die es tut) und die **Beobachtung** (beliebig viele, die es
  angeht). Wer anlegt, beobachtet automatisch — deshalb verliert man eine
  weitergegebene Aufgabe nicht aus dem Blick.

  Eine Aufgabe bleibt privat, solange genau ein Mensch an ihr hängt. Sobald
  ein zweiter dazukommt, gleich auf welchem Weg, steht sie im
  Fraktionsboard. Durchgesetzt wird das auf dem Server, nicht hier —
  Ausblenden wäre keine Vertraulichkeit, sondern eine Bitte.
*/

const BRETTER = [
  { schluessel: "meins", titel: "Dein Board", symbol: Liste },
  { schluessel: "fraktion", titel: "Fraktionsboard", symbol: Raster },
];

export default function AufgabenAnsicht({ oeffne, aufgabeId }) {
  const { zustand, ich } = useDemo();
  const [brett, setzeBrett] = useState("meins");
  const [karte, setzeKarte] = useState(null);

  /*
    Wer aus dem Kanal auf „Aufgabe: …“ tippt, will die Karte sehen und nicht
    das Brett. Und zwar im richtigen Reiter: Ist man selbst nicht zuständig,
    steht sie nur im Fraktionsboard.

    Nur beim Ankommen, nicht bei jeder späteren Änderung — sonst spränge die
    Karte wieder auf, sobald man sie zumacht.
  */
  const gekommenVon = useRef(null);
  useEffect(() => {
    if (!aufgabeId || gekommenVon.current === aufgabeId) return;
    gekommenVon.current = aufgabeId;
    const gesucht = zustand.aufgaben.find((a) => a.id === aufgabeId);
    setzeBrett(gesucht && gehoertZuMir(gesucht, ich.id) ? "meins" : "fraktion");
    setzeKarte(aufgabeId);
  }, [aufgabeId, zustand.aufgaben, ich.id]);

  const meine = zustand.aufgaben.filter((a) => gehoertZuMir(a, ich.id));
  const offen = meine.filter((a) => !a.erledigt);
  /* Was ich nur beobachte, steht mit im Brett — aber ich mache es nicht. */
  const beiAnderen = offen.filter((a) => a.zustaendigId !== ich.id).length;
  const gemeinsam = zustand.aufgaben.filter((a) => a.sichtbarkeit === "fraktion");

  const reiter = [
    { ...BRETTER[0], anzahl: offen.length },
    { ...BRETTER[1], anzahl: gemeinsam.filter((a) => !a.erledigt).length },
  ];

  return (
    <>
      <div className="detailkopf">
        <p className="detailkopf__vorspann">Fraktion Bürgerforum</p>
        <h1 className="detailkopf__titel">Aufgaben</h1>
        <p className="klein leise" style={{ marginTop: "var(--luft-2)" }}>
          {offen.length === 0
            ? "Für dich ist nichts offen."
            : `${offen.length} von ${meine.length} noch offen${
                beiAnderen > 0 ? `, davon ${beiAnderen} bei jemand anderem` : ""
              }.`}{" "}
          Eine Karte antippen öffnet sie; halten und ziehen verschiebt sie.
        </p>
      </div>

      <Reiter reiter={reiter} aktiv={brett} aufWechsel={setzeBrett} />

      <Tafel schluessel="meins" aktiv={brett}>
        <Aufgabenbrett brett="meins" aufKarte={setzeKarte} />
      </Tafel>

      <Tafel schluessel="fraktion" aktiv={brett}>
        {gemeinsam.length === 0 && (
          <p className="leise" style={{ marginBottom: "var(--luft-4)" }}>
            Hier steht, was mehrere betrifft. Sobald jemand eine Aufgabe einem anderen gibt,
            erscheint sie in diesem Brett.
          </p>
        )}
        <Aufgabenbrett brett="fraktion" aufKarte={setzeKarte} />
      </Tafel>

      {karte && (
        <Aufgabenkarte
          aufgabeId={karte}
          aufSchliessen={() => setzeKarte(null)}
          oeffne={oeffne}
        />
      )}
    </>
  );
}
